import { useState, useRef } from "react";
import { format, isPast, isToday, isTomorrow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Task, TaskLabel } from "@/types/task-management";
import { mockTaskLabels } from "@/lib/mock/task-management";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Calendar, 
  Clock, 
  Flag, 
  MoreHorizontal, 
  Tag, 
  Briefcase, 
  Building2,
  Sun,
  SunDim,
  ArrowRight
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import confetti from 'canvas-confetti';

interface TaskListItemProps {
  task: Task;
  onStatusChange: (status: string) => void;
  onClick: () => void;
  onAddToMyDay?: (added: boolean) => void;
  onDelete?: () => void;
  onPostpone?: () => void;
}

export default function TaskListItem({ task, onStatusChange, onClick, onAddToMyDay, onDelete, onPostpone }: TaskListItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const checkboxRef = useRef<HTMLButtonElement>(null);
  
  // Formatar data de vencimento
  const formatDueDate = (dateString?: string) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return "Hoje";
    } else if (isTomorrow(date)) {
      return "Amanhã";
    } else {
      return format(date, "dd 'de' MMMM", { locale: ptBR });
    }
  };
  
  // Verificar se a tarefa está atrasada
  const isOverdue = task.due_date ? isPast(new Date(task.due_date)) && task.status !== "completed" : false;
  
  // Obter labels da tarefa
  const taskLabels = task.labels
    .map(labelId => mockTaskLabels.find(label => label.id === labelId))
    .filter(label => label !== undefined) as TaskLabel[];
  
  // Determinar a cor da prioridade
  const getPriorityColor = () => {
    switch (task.priority) {
      case "urgent": return "text-red-600";
      case "high": return "text-orange-500";
      case "medium": return "text-yellow-500";
      case "low": return "text-blue-500";
      default: return "text-gray-500";
    }
  };
  
  // Determinar o texto da prioridade
  const getPriorityText = () => {
    switch (task.priority) {
      case "urgent": return "Urgente";
      case "high": return "Alta";
      case "medium": return "Média";
      case "low": return "Baixa";
      default: return "Sem prioridade";
    }
  };
  
  // Função para disparar o efeito de confete
  const triggerConfetti = () => {
    if (!checkboxRef.current) return;
    
    const rect = checkboxRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { x, y },
      colors: ['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800'],
      zIndex: 9999,
    });
  };
  
  // Manipulador de alteração de status
  const handleStatusChange = (status: string) => {
    if (status === "completed" && task.status !== "completed") {
      // Disparar confete apenas quando marcar como concluído
      setTimeout(() => triggerConfetti(), 100);
    }
    onStatusChange(status);
  };
  
  return (
    <div 
      className={`py-1.5 px-2 flex items-center cursor-pointer hover:bg-gray-50/50 transition-colors border-b border-gray-100 ${task.status === "completed" ? "text-gray-400" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div className="flex-shrink-0 mr-2">
        <Checkbox 
          ref={checkboxRef}
          checked={task.status === "completed"}
          onClick={(e) => {
            e.stopPropagation();
            handleStatusChange(task.status === "completed" ? "todo" : "completed");
          }}
          className={task.status === "completed" ? "" : getPriorityColor()}
        />
      </div>
      
      <div className="flex-grow min-w-0 overflow-hidden">
        <div className="flex items-center">
          <h3 className={`font-extrabold truncate ${task.status === "completed" ? "line-through text-gray-400" : ""}`}>
            {task.title}
          </h3>
          
          {task.added_to_my_day && (
            <Sun className="h-3.5 w-3.5 text-amber-500 ml-2 flex-shrink-0" />
          )}
        </div>
        
        {/* Linha de informações adicionais */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
          {/* Data de vencimento */}
          {task.due_date && (
            <div className={`flex items-center gap-1 ${isOverdue ? "text-red-600 font-extrabold" : ""}`}>
              <Calendar className="h-3 w-3 flex-shrink-0" />
              {formatDueDate(task.due_date)}
            </div>
          )}
          
          {/* Playbook como tag simples */}
          {task.playbook_name && (
            <div className="flex items-center gap-1">
              <Briefcase className="h-3 w-3 flex-shrink-0" />
              <span className="truncate max-w-[100px]">{task.playbook_name}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className={`flex items-center gap-1 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
        {/* Prioridade alinhada à direita */}
        {task.priority !== "low" && (
          <div className={`flex items-center gap-1 ${getPriorityColor()}`}>
            <Flag className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="text-xs truncate hidden sm:inline">{getPriorityText()}</span>
          </div>
        )}
        
        {/* Botão "Adicionar ao meu dia" */}
        {onAddToMyDay && !task.added_to_my_day && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-gray-400 hover:text-amber-500"
            onClick={(e) => {
              e.stopPropagation();
              onAddToMyDay(true);
            }}
          >
            <SunDim className="h-3.5 w-3.5" />
          </Button>
        )}
        
        {/* Menu de ações */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7 text-gray-400 hover:text-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem>Editar</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => {
              e.stopPropagation();
              onAddToMyDay && onAddToMyDay(!task.added_to_my_day);
            }}>
              {task.added_to_my_day ? "Remover do meu dia" : "Adicionar ao meu dia"}
            </DropdownMenuItem>
            {onPostpone && (
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                onPostpone();
              }}>
                Prorrogar tarefa
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {onDelete && (
              <DropdownMenuItem 
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="text-red-600"
              >
                Excluir tarefa
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
} 